import { createContext, useEffect, useContext, useMemo, useState, type ReactNode } from "react";

export type Lang = "en" | "ar";
export interface Bilingual {
  en: string;
  ar: string;
}

const KEY = "wfos.lang";

function initial(): Lang {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "en" || saved === "ar") return saved;
  } catch {
    // storage may be unavailable (private window)
  }
  return typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("ar") ? "ar" : "en";
}

interface LangState {
  lang: Lang;
  dir: "ltr" | "rtl";
  setLang: (l: Lang) => void;
  t: (b: Bilingual) => string;
}

const Ctx = createContext<LangState | null>(null);

/** One language for the whole app, remembered per device (NEW-UX-10). */
export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initial);
  const value = useMemo<LangState>(
    () => ({
      lang,
      dir: lang === "ar" ? "rtl" : "ltr",
      setLang: (l) => {
        setLangState(l);
        try {
          localStorage.setItem(KEY, l);
        } catch {
          // not remembered; the choice still holds for this visit
        }
      },
      t: (b) => (lang === "ar" ? b.ar || b.en : b.en),
    }),
    [lang],
  );
  // The whole document turns, so the browser, screen readers and the
  // scrollbar agree with the app's direction (demo-ready Task 10).
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang(): LangState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useLang outside LangProvider");
  return v;
}

/** Words the app itself says, not the server. */
export const UI = {
  thread: { en: "Thread", ar: "المحادثة" },
  queue: { en: "Queue", ar: "القرارات" },
  reports: { en: "Reports", ar: "التقارير" },
  switchLang: { en: "ع", ar: "EN" },
  send: { en: "Send", ar: "إرسال" },
  howMade: { en: "How this was made", ar: "كيف أُعدّ هذا" },
  downloadPack: { en: "Download the pack", ar: "تنزيل التقرير" },
  file_pptx: { en: "Slides", ar: "شرائح" },
  file_xlsx: { en: "Excel", ar: "Excel" },
  file_docx: { en: "Word", ar: "Word" },
  file_pdf: { en: "PDF", ar: "PDF" },
  approve: { en: "Approve", ar: "موافقة" },
  reject: { en: "Reject", ar: "رفض" },
  change: { en: "Needs more", ar: "يحتاج المزيد" },
  reasonPlaceholder: { en: "Say why, in a sentence", ar: "اذكر السبب في جملة" },
  record: { en: "Record decision", ar: "سجّل القرار" },
  recording: { en: "Recording…", ar: "جارٍ التسجيل…" },
  decided: { en: "Decided", ar: "تقرر" },
  goAhead: { en: "Go ahead", ar: "تابع" },
  notNow: { en: "Not now", ar: "ليس الآن" },
  attach: { en: "Attach a draft", ar: "إرفاق مسودة" },
  nothingWaiting: { en: "Nothing is waiting on you.", ar: "لا شيء بانتظارك." },
  nothingYet: { en: "Nothing yet — ask for something in the thread.", ar: "لا شيء بعد — اطلب شيئاً في المحادثة." },
  back: { en: "Back to the thread", ar: "العودة إلى المحادثة" },
  stillWorking: { en: "still working", ar: "ما زال يعمل" },
  seconds: { en: "s", ar: " ث" },
  openConsole: { en: "Console", ar: "لوحة التحكم" },
  signOut: { en: "Sign out", ar: "تسجيل الخروج" },
  sources: { en: "Sources", ar: "المصادر" },
  newTopic: { en: "New topic", ar: "موضوع جديد" },
  browseAgents: { en: "Browse agents", ar: "تصفح الوكلاء" },
  workingOut: { en: "Working out what you need…", ar: "جارٍ فهم ما تحتاجه…" },
  marked: {
    en: "This contains regulated personal data, so it is not shown in the thread. Open it to read.",
    ar: "يحتوي هذا على بيانات شخصية خاضعة للتنظيم، لذلك لا يُعرض في المحادثة. افتحه للقراءة.",
  },
  draftHeld: {
    en: "The draft did not pass its checks, so it is not shown here. The figures below are the calculation's own.",
    ar: "لم تجتز المسودة فحوصها، لذلك لا تُعرض هنا. الأرقام أدناه من الحساب نفسه.",
  },
} satisfies Record<string, Bilingual>;

/** Western digits as Arabic-Indic. */
export const arabicDigits = (n: number): string => String(n).replace(/\d/g, (d) => String.fromCharCode(0x0660 + Number(d)));

/**
 * An Arabic count with its noun in the right form: one and two are said by
 * the noun itself, three to ten take the plural, eleven and up the singular
 * accusative ("١١ قرارًا").
 */
export function arabicCount(n: number, forms: { one: string; two: string; few: string; many: string }): string {
  if (n === 1) return forms.one;
  if (n === 2) return forms.two;
  const mod = n % 100;
  if (mod >= 3 && mod <= 10) return `${arabicDigits(n)} ${forms.few}`;
  return `${arabicDigits(n)} ${forms.many}`;
}

/** The forms of "decision", used by every count of decisions. */
export const DECISIONS_AR = { one: "قرار واحد", two: "قراران", few: "قرارات", many: "قرارًا" };

/** A composed report's link in the language the person reads; a pack's link as it is (packs have one language). */
export function fileHref(url: string, lang: "en" | "ar"): string {
  return /\/report\.(pptx|xlsx|docx|pdf)$/.test(url) ? `${url}?lang=${lang}` : url;
}
